import {lineAnchorFrom, lineOrientationFrom} from '@github-ui/diffs/diff-line-helpers'
import type {DiffAnchor} from '@github-ui/diffs/types'
import React, {useCallback, useRef} from 'react'

import {ContentCell} from './grid-cells/ContentCell'
import {LineNumberCell} from './grid-cells/LineNumberCell'
import {EmptyCell} from './grid-cells/EmptyCell'
import {MarkersDialogContextProvider} from '../contexts/MarkersDialogContext'
import {isEmptyDiffLine} from '../helpers/line-helpers'
import type {FileDiffReference, DiffLine, ClientDiffLine} from '../types'
import {useMarkersDialogStatus} from '../hooks/use-markers-dialog-status'
import {HiddenUnicodeAlert} from '@github-ui/hidden-unicode-banner/HiddenUnicodeAlert'
import type {HunkData} from '../helpers/hunk-data-helpers'
import type {DiffAnnotation, Thread, ViewerData} from '../conversation-types'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import {DiffCodeAlert} from './DiffCodeAlert'

const CodeDiffLineUnmemoized = ({
  annotationsAtPosition,
  commentsPreference,
  copilotChatReference,
  currentHunk,
  diffAnchor,
  diffLine,
  filePath,
  lineAnchor,
  handleDiffSideCellSelectionBlocking,
  hasHiddenUnicodeCharacters,
  isLeftSide,
  isRowSelected,
  isSplit,
  nextHunk,
  previousHunk,
  rowId,
  threadsAtPosition,
  viewerData,
}: {
  annotationsAtPosition: DiffAnnotation[]
  commentsPreference: CommentsPreference | undefined
  copilotChatReference?: FileDiffReference
  currentHunk: HunkData | undefined
  diffAnchor: DiffAnchor
  diffLine: ClientDiffLine
  filePath: string
  lineAnchor: string
  handleDiffSideCellSelectionBlocking?: (diffSide: 'left' | 'right') => void
  hasHiddenUnicodeCharacters: boolean
  isLeftSide: boolean
  isRowSelected: boolean
  isSplit: boolean
  nextHunk: HunkData | undefined
  previousHunk: HunkData | undefined
  rowId: string
  threadsAtPosition: Thread[]
  viewerData: ViewerData
}) => {
  const contentRef = useRef<HTMLTableCellElement>(null)
  const line = diffLine as DiffLine

  /*
   * display the left cell for all lines in a unified view, and left lines of a split view
   * this cell will not contain a number in the unified view if the line is an addition
   */
  const showLeftNumber = line.type !== 'ADDITION'
  const showLeftNumberCell = !isSplit || isLeftSide

  /*
   * display the right cell for all lines in a unified view, and right lines of a split view
   * this cell will not contain a number in the unified view if the line is a deletion
   */
  const showRightNumber = line.type !== 'DELETION'
  const showRightNumberCell = !isSplit || !isLeftSide

  const handleRightDiffSideCellSelectionBlocking = useCallback(() => {
    if (!handleDiffSideCellSelectionBlocking) return
    const diffSideToBlock = 'left'
    handleDiffSideCellSelectionBlocking(diffSideToBlock)
  }, [handleDiffSideCellSelectionBlocking])

  const handleLeftDiffSideCellSelectionBlocking = useCallback(() => {
    if (!handleDiffSideCellSelectionBlocking) return
    const diffSideToBlock = 'right'
    handleDiffSideCellSelectionBlocking(diffSideToBlock)
  }, [handleDiffSideCellSelectionBlocking])

  const contentColumnIndex = isSplit ? (isLeftSide ? 1 : 3) : 2

  /**
   * The `markersStatus` state tracks whether the user has manually expanded/minimized
   * inline comment markers in the diff view while the "Collapsed" CommentsPreference setting is enabled
   * This state is critical for preserving a mouse user's intent regarding the visibility of
   * inline markers during their interaction with the diff view while in CommentsPreference is Collapsed.
   */
  const [markersStatus, dispatchMarkersStatus] = useMarkersDialogStatus(
    commentsPreference ?? CommentsPreference.Visible,
  )

  return (
    <MarkersDialogContextProvider>
      {showLeftNumberCell && (
        <LineNumberCell
          threadsAtPosition={threadsAtPosition}
          annotationsAtPosition={annotationsAtPosition}
          copilotChatReference={copilotChatReference}
          columnIndex={0}
          contentColumnIndex={contentColumnIndex}
          contentRef={contentRef}
          diffAnchor={diffAnchor}
          diffLine={diffLine}
          dispatchMarkersStatus={dispatchMarkersStatus}
          filePath={filePath}
          handleDiffSideCellSelectionBlocking={handleLeftDiffSideCellSelectionBlocking}
          isLeftSide={isLeftSide}
          isRowSelected={isRowSelected}
          isSplit={isSplit}
          rowId={rowId}
        >
          {hasHiddenUnicodeCharacters && !showRightNumber && (
            <DiffCodeAlert>
              <HiddenUnicodeAlert />
            </DiffCodeAlert>
          )}
          {showLeftNumber && line.left}
        </LineNumberCell>
      )}
      {showRightNumberCell && (
        <LineNumberCell
          annotationsAtPosition={annotationsAtPosition}
          copilotChatReference={copilotChatReference}
          columnIndex={isSplit ? 2 : 1}
          contentColumnIndex={contentColumnIndex}
          contentRef={contentRef}
          diffAnchor={diffAnchor}
          diffLine={diffLine}
          dispatchMarkersStatus={dispatchMarkersStatus}
          filePath={filePath}
          handleDiffSideCellSelectionBlocking={handleRightDiffSideCellSelectionBlocking}
          isLeftSide={isLeftSide}
          isRowSelected={isRowSelected}
          isSplit={isSplit}
          rowId={rowId}
          threadsAtPosition={threadsAtPosition}
        >
          {hasHiddenUnicodeCharacters && showRightNumber && (
            <DiffCodeAlert>
              <HiddenUnicodeAlert />
            </DiffCodeAlert>
          )}
          {showRightNumber && line.right}
        </LineNumberCell>
      )}
      <ContentCell
        annotationsAtPosition={annotationsAtPosition}
        commentsPreference={commentsPreference}
        copilotChatReference={copilotChatReference}
        columnIndex={contentColumnIndex}
        currentHunk={currentHunk}
        diffAnchor={diffAnchor}
        diffLine={diffLine}
        dispatchMarkersStatus={dispatchMarkersStatus}
        filePath={filePath}
        lineAnchor={lineAnchor}
        handleDiffSideCellSelectionBlocking={
          isLeftSide ? handleLeftDiffSideCellSelectionBlocking : handleRightDiffSideCellSelectionBlocking
        }
        isLeftSide={isLeftSide}
        isRowSelected={isRowSelected}
        isSplit={isSplit}
        markersStatus={markersStatus}
        nextHunk={nextHunk}
        previousHunk={previousHunk}
        ref={contentRef}
        rowId={rowId}
        threadsAtPosition={threadsAtPosition}
        viewerData={viewerData}
      />
    </MarkersDialogContextProvider>
  )
}

interface DiffSideProps {
  annotationsAtPosition: DiffAnnotation[]
  commentsPreference: CommentsPreference | undefined
  copilotChatReference?: FileDiffReference
  currentHunk: HunkData | undefined
  diffLine: ClientDiffLine
  diffAnchor: DiffAnchor
  filePath: string
  handleDiffSideCellSelectionBlocking: (diffSide: 'left' | 'right') => void
  hasHiddenUnicodeCharacters: boolean
  isLeftSide: boolean
  isRowSelected: boolean
  isSplit: boolean
  nextHunk: HunkData | undefined
  previousHunk: HunkData | undefined
  rowId: string
  threadsAtPosition: Thread[]
  viewerData: ViewerData
}

export const DiffLineSideUnmemoized = ({
  annotationsAtPosition,
  commentsPreference,
  copilotChatReference,
  currentHunk,
  diffLine,
  diffAnchor,
  filePath,
  handleDiffSideCellSelectionBlocking,
  hasHiddenUnicodeCharacters,
  isLeftSide,
  isRowSelected,
  isSplit,
  nextHunk,
  previousHunk,
  rowId,
  threadsAtPosition,
  viewerData,
}: DiffSideProps) => {
  if (isEmptyDiffLine(diffLine)) {
    return (
      <>
        <EmptyCell
          columnIndex={isLeftSide ? 0 : 2}
          diffAnchor={diffAnchor}
          diffLine={diffLine}
          isLeftSide={isLeftSide}
          isSplit={isSplit}
        />
        <EmptyCell
          columnIndex={isLeftSide ? 1 : 3}
          diffAnchor={diffAnchor}
          diffLine={diffLine}
          isLeftSide={isLeftSide}
          isSplit={isSplit}
        />
      </>
    )
  }

  const lineOrientation = isLeftSide ? 'left' : lineOrientationFrom(diffLine.type)
  const lineNumber = lineOrientation === 'left' ? diffLine.left : diffLine.right
  const lineAnchor = lineAnchorFrom(diffAnchor, lineOrientation, lineNumber ?? 0)

  switch (diffLine.type) {
    case 'HUNK':
      return null
    case 'ADDITION':
    case 'DELETION':
    case 'CONTEXT':
    case 'INJECTED_CONTEXT':
      return (
        <CodeDiffLine
          annotationsAtPosition={annotationsAtPosition}
          commentsPreference={commentsPreference}
          copilotChatReference={copilotChatReference}
          currentHunk={currentHunk}
          diffAnchor={diffAnchor}
          diffLine={diffLine}
          filePath={filePath}
          handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
          hasHiddenUnicodeCharacters={hasHiddenUnicodeCharacters}
          isLeftSide={isLeftSide}
          isRowSelected={isRowSelected}
          isSplit={isSplit}
          lineAnchor={lineAnchor}
          nextHunk={nextHunk}
          previousHunk={previousHunk}
          rowId={rowId}
          threadsAtPosition={threadsAtPosition}
          viewerData={viewerData}
        />
      )
    default:
      throw new Error(`cannot handle type ${diffLine.type}`)
  }
}

export const DiffLineSide = React.memo(DiffLineSideUnmemoized)
export const CodeDiffLine = React.memo(CodeDiffLineUnmemoized)

try{ CodeDiffLineUnmemoized.displayName ||= 'CodeDiffLineUnmemoized' } catch {}
try{ DiffLineSideUnmemoized.displayName ||= 'DiffLineSideUnmemoized' } catch {}
try{ DiffLineSide.displayName ||= 'DiffLineSide' } catch {}
try{ CodeDiffLine.displayName ||= 'CodeDiffLine' } catch {}